import React, { useState, useEffect } from 'react';
import { MapPin, Users, DollarSign, Clock } from 'lucide-react';
import axiosInstance from '../../utils/axiosConfig';

const ActiveDrivers = () => {
  const [driversData, setDriversData] = useState({
    drivers: [],
    stats: {
      totalActiveDrivers: 0,
      averageRides: 0,
      totalEarnings: 0,
      availableForPickup: 0
    }
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchActiveDrivers();
    // Auto-refresh every 30 seconds
    const interval = setInterval(fetchActiveDrivers, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchActiveDrivers = async () => {
    try {
      const response = await axiosInstance.get('/driver/active');
      if (response.data.success) {
        setDriversData(response.data.data);
        setError(null);
      } else {
        setError('Fehler beim Laden der Fahrer: ' + (response.data.message || 'Unbekannter Fehler'));
      }
    } catch (err) {
      setError('Fehler beim Laden der aktiven Fahrer: ' + err.message);
      console.error('Error fetching active drivers:', err);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'AVAILABLE': return 'text-green-600 bg-green-100';
      case 'BUSY': return 'text-yellow-600 bg-yellow-100';
      case 'ON_BREAK': return 'text-blue-600 bg-blue-100';
      case 'OFFLINE': return 'text-gray-600 bg-gray-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'AVAILABLE': return 'Verfügbar';
      case 'BUSY': return 'Beschäftigt';
      case 'ON_BREAK': return 'Pause';
      case 'OFFLINE': return 'Offline';
      default: return 'Unbekannt';
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 rounded mb-4"></div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="bg-gray-100 h-20 rounded"></div>
            ))}
          </div>
          <div className="space-y-4">
            {[1, 2, 3].map(i => (
              <div key={i} className="bg-gray-100 h-16 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-semibold text-gray-800 flex items-center">
          <Users className="mr-2 text-blue-600" size={24} />
          Aktive Fahrer
        </h2>
        <button
          onClick={fetchActiveDrivers}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          Aktualisieren
        </button>
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-100 text-red-700 rounded-lg">
          {error}
        </div>
      )}

      {/* Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-blue-50 p-4 rounded-lg">
          <div className="flex items-center">
            <Users className="text-blue-600 mr-2" size={20} />
            <div>
              <p className="text-sm text-gray-600">Aktive Fahrer</p>
              <p className="text-2xl font-bold text-blue-600">
                {driversData.stats.totalActiveDrivers}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-green-50 p-4 rounded-lg">
          <div className="flex items-center">
            <MapPin className="text-green-600 mr-2" size={20} />
            <div>
              <p className="text-sm text-gray-600">Verfügbar</p>
              <p className="text-2xl font-bold text-green-600">
                {driversData.stats.availableForPickup}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-yellow-50 p-4 rounded-lg">
          <div className="flex items-center">
            <Clock className="text-yellow-600 mr-2" size={20} />
            <div>
              <p className="text-sm text-gray-600">Ø Fahrten</p>
              <p className="text-2xl font-bold text-yellow-600">
                {driversData.stats.averageRides.toFixed(1)}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-purple-50 p-4 rounded-lg">
          <div className="flex items-center">
            <DollarSign className="text-purple-600 mr-2" size={20} />
            <div>
              <p className="text-sm text-gray-600">Gesamtverdienst</p>
              <p className="text-2xl font-bold text-purple-600">
                ₺{driversData.stats.totalEarnings.toLocaleString()}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Drivers List */}
      <div className="space-y-4">
        <h3 className="text-lg font-medium text-gray-800">Online Fahrer</h3>
        
        {driversData.drivers.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <Users size={48} className="mx-auto mb-4 text-gray-300" />
            <p className="text-lg">Keine aktiven Fahrer gefunden</p>
            <p className="text-sm">Fahrer werden hier angezeigt, sobald sie online sind</p>
          </div>
        ) : (
          driversData.drivers.map((driver) => (
            <div key={driver.id} className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50 transition-colors">
              <div className="flex items-center space-x-4">
                <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center text-white font-semibold">
                  {driver.first_name?.[0]}{driver.last_name?.[0]}
                </div>
                <div>
                  <h4 className="font-medium text-gray-800">
                    {driver.first_name} {driver.last_name}
                  </h4>
                  <p className="text-sm text-gray-600">{driver.email}</p>
                  {driver.last_seen && (
                    <p className="text-xs text-gray-500">
                      Zuletzt gesehen: {new Date(driver.last_seen).toLocaleString('de-DE')}
                    </p>
                  )}
                </div>
              </div>

              <div className="flex items-center space-x-4">
                {driver.location_lat && driver.location_lng && (
                  <div className="text-sm text-gray-600">
                    <MapPin size={16} className="inline mr-1" />
                    {driver.location_lat.toFixed(4)}, {driver.location_lng.toFixed(4)}
                  </div>
                )}
                
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(driver.status)}`}>
                  {getStatusText(driver.status)}
                </span>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default ActiveDrivers;
